import MillDetailsWeather from "./MillDetailsWeather/MillDetailsWeather.view";
import { useStyles } from "./MillDetails.styles";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { CORPORATE_BACKOFFICE_PATHS, DefaultTranslationFieldsInTable } from "constants/constants";
import useRolesPermissions from "hooks/useRolesPermissions";
import { translateDefaultEntity } from "utilities/localesUtilities";

const MillDetails = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { millId } = useParams<{ millId: string }>();
    const { loading, millData } = MillsEntity.GetAllMillData(Number(millId));

    const { CORPORATE_PERMISSIONS } = useRolesPermissions();

    if (loading && !millData) {
        return (
            <div className={`${classes.mainWrapper} ${classes.mainWrapperLoading}`}>
                <CircularProgress color="info" />
            </div>
        );
    }

    return (
        <div className={classes.detailsWrapper}>
            <MillDetailsWeather />
            <div className={classes.mainWrapper}>
                <div className={classes.mainWrapperHeader}>
                    <div className={classes.mainWrapperHeaderTitle}>{t("locations.mill_details.title")}</div>
                    {CORPORATE_PERMISSIONS?.corporationPermissions?.canEditSettings && (
                        <IconButton
                            onClick={() =>
                                navigate(
                                    `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.SETUP_DASHBOARD}?millId=${millId}`,
                                )
                            }
                        >
                            <EditIcon />
                        </IconButton>
                    )}
                </div>
                <div className={classes.informationWrapper}>
                    <div className={classes.informationLeft}>
                        <div className={classes.informationItem}>
                            <div className={classes.label}>{t("locations.mill_details.location_name")}</div>
                            <div className={classes.value}>{millData?.mill.name}</div>
                        </div>
                        <div className={classes.informationItem}>
                            <div className={classes.label}>{t("locations.mill_details.mill_code")}</div>
                            <div className={classes.value}>{millData?.mill.code || "--"}</div>
                        </div>
                        <div className={classes.informationItem}>
                            <div className={classes.label}>{t("locations.mill_details.division")}</div>
                            <div className={classes.value}>{millData?.mill.division?.name || "--"}</div>
                        </div>
                        <div className={classes.informationItem}>
                            <div className={classes.label}>{t("locations.mill_details.region")}</div>
                            <div className={classes.value}>{millData?.mill.region?.name || "--"}</div>
                        </div>
                        <div className={classes.informationItem}>
                            <div className={classes.label}>{t("locations.mill_details.physical_address")}</div>
                            <div className={classes.value}>{millData?.mill.physical_address || "--"}</div>
                        </div>
                        <div className={classes.informationItem}>
                            <div className={classes.label}>{t("locations.mill_details.country")}</div>
                            <div className={classes.value}>{millData?.mill.country?.name || "--"}</div>
                        </div>
                    </div>
                    <div className={classes.informationRight}>
                        <div className={`${classes.label} ${classes.labelWithSpace}`}>
                            {t("locations.mill_details.admins")}
                        </div>
                        {millData?.users.map((user) => (
                            <div className={classes.userWrapper} key={user.user_id}>
                                <div className={classes.userTitle}>{`${user.first_name} ${user.last_name}`}</div>
                                <a href={`mailto:${user.email}`} className={classes.userEmail}>
                                    {user.email}
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
                <div className={classes.sectionWrapper}>
                    <div className={classes.mainWrapperHeaderTitle}>{t("locations.mill_details.shifts")}</div>
                    <div className={classes.shiftsTableWrapper}>
                        {millData?.mill.shifts?.map((shift) => (
                            <div className={classes.shiftWrapper} key={shift.shift_id}>
                                <div className={classes.shiftTitle}>{shift.name}</div>
                                <div
                                    className={
                                        shift.shift_historic[0]?.shift_days?.length
                                            ? classes.shiftTable
                                            : classes.shiftTableNoShift
                                    }
                                >
                                    {shift.shift_historic[0]?.shift_days?.length ? (
                                        shift.shift_historic[0]?.shift_days.map((shiftDay) =>
                                            shiftDay.is_enabled ? (
                                                <div className={classes.shiftTableItem} key={shiftDay.day_id}>
                                                    <div className={`${classes.label} ${classes.shiftTableLabel}`}>
                                                        {translateDefaultEntity(
                                                            i18n.language,
                                                            shiftDay.day,
                                                            "name",
                                                            DefaultTranslationFieldsInTable.DAY,
                                                        )}
                                                    </div>
                                                    <div className={classes.shiftTime}>
                                                        <div className={classes.value}>{`${
                                                            shift.shift_historic[0]?.start_time
                                                        } ${t("locations.mill_details.to")} ${
                                                            shift.shift_historic[0]?.end_time
                                                        }`}</div>
                                                    </div>
                                                </div>
                                            ) : null,
                                        )
                                    ) : (
                                        <div className={classes.shiftTableNoShiftText}>
                                            {t("locations.mill_details.no_shifts_scheduled")}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className={classes.sectionWrapper}>
                    <div className={classes.mainWrapperHeaderTitle}>{t("locations.mill_details.settings")}</div>
                    <div className={classes.settingsWrapper}>
                        <div className={classes.informationItem}>
                            <div className={classes.label}>{t("locations.mill_details.language")}</div>
                            <div className={classes.value}>{millData?.mill?.mill_data?.language?.name}</div>
                        </div>
                        <div className={classes.informationItem}>
                            <div className={classes.label}>
                                {t("locations.mill_details.first_day_of_reporting_week")}
                            </div>
                            <div className={classes.value}>
                                {millData?.days.find(
                                    (day) => day.day_id === millData?.mill?.mill_data?.first_day_reporting_week,
                                )
                                    ? translateDefaultEntity(
                                          i18n.language,
                                          millData?.mill?.mill_data?.day_reporting_week,
                                          "name",
                                          DefaultTranslationFieldsInTable.DAY,
                                      )
                                    : "--"}
                            </div>
                        </div>
                        <div className={classes.informationItem}>
                            <div className={classes.label}>{t("locations.mill_details.first_day_of_fiscal_year")}</div>
                            <div className={classes.value}>
                                {millData?.mill?.mill_data?.first_day_fiscal_year || "--"}
                            </div>
                        </div>
                        <div className={classes.informationItem}>
                            <div className={classes.label}>{t("locations.mill_details.timezone")}</div>
                            <div className={classes.value}>{millData?.mill?.mill_data?.timezone}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MillDetails;
